'use client'

import { isNil } from 'lodash-es'
import type { Schema } from 'next-safe-action/adapters/types'
import {
  type HookBaseUtils,
  type HookCallbacks,
  type HookSafeActionFn,
  type UseActionHookReturn,
  useAction as useSafeAction,
} from 'next-safe-action/hooks'

import { type ApiClientResult } from '@/shared/api'
import { emitAirbridgeObject } from '@/shared/config/airbridge'

type UnwrapApiResult<T> = T extends ApiClientResult<infer U> ? U : T

export const useAction = <
  ServerError,
  S extends Schema | undefined,
  const BAS extends readonly Schema[],
  CVE,
  CBAVE,
  Data,
>(
  safeActionFn: HookSafeActionFn<ServerError, S, BAS, CVE, CBAVE, Data>,
  utils?: HookBaseUtils<S> &
    HookCallbacks<ServerError, S, BAS, CVE, CBAVE, UnwrapApiResult<Data>>,
): UseActionHookReturn<ServerError, S, BAS, CVE, CBAVE, Data> => {
  return useSafeAction<ServerError, S, BAS, CVE, CBAVE, Data>(safeActionFn, {
    ...utils,
    onSuccess: args => {
      const raw = args.data as ApiClientResult<unknown> | undefined

      if (!isNil(raw?.airbridgeObject)) {
        emitAirbridgeObject(raw.airbridgeObject)
      }

      utils?.onSuccess?.({ ...args, data: raw?.data } as never)
    },
  } as HookBaseUtils<S> & HookCallbacks<ServerError, S, BAS, CVE, CBAVE, Data>)
}
